
import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, MapPin, Grape, Trash2, X, Map as MapIcon, Pencil, ArrowRight, Ruler } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import MapPicker from "../components/MapPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Il "value" resta il testo italiano storico: e' quello gia' salvato come
// phenology_phase nei documenti esistenti (vigneti e trattamenti), non va
// cambiato per non rompere il confronto con i dati in DB. Lo slug serve solo
// come chiave di traduzione per la label mostrata all'utente.
const PHENOLOGY_SLUGS = [
  ["Riposo vegetativo", "dormancy"],
  ["Pianto", "sap_bleeding"],
  ["Germogliamento", "budburst"],
  ["Foglie distese", "leaf_unfolding"],
  ["Pre-fioritura", "pre_flowering"],
  ["Fioritura", "flowering"],
  ["Allegagione", "fruit_set"],
  ["Pre-chiusura grappolo", "bunch_closure"],
  ["Invaiatura", "veraison"],
  ["Maturazione", "ripening"],
  ["Vendemmia", "harvest"],
  ["Caduta foglie", "leaf_fall"],
];

export function getPhenologyPhases(t) {
  return PHENOLOGY_SLUGS.map(([value, slug]) => ({ value, label: t(`phenology.${slug}`) }));
}

export function getPhenologyLabel(value, t) {
  const entry = PHENOLOGY_SLUGS.find(([v]) => v === value);
  return entry ? t(`phenology.${entry[1]}`) : value;
}

export function formatArea(sqm) {
  if (sqm === null || sqm === undefined || sqm === "" || isNaN(sqm)) return null;
  const v = Number(sqm);
  if (v <= 0) return null;
  if (v >= 10000) return { primary: `${(v / 10000).toFixed(2).replace(".", ",")} ha`, secondary: `${Math.round(v).toLocaleString("it-IT")} m²` };
  if (v >= 100) return { primary: `${(v / 100).toFixed(1).replace(".", ",")} are`, secondary: `${Math.round(v).toLocaleString("it-IT")} m²` };
  return { primary: `${Math.round(v).toLocaleString("it-IT")} m²`, secondary: null };
}

function toSqm(value, unit) {
  const n = parseFloat(String(value).replace(",", "."));
  if (!Number.isFinite(n) || n <= 0) return null;
  if (unit === "ha") return n * 10000;
  if (unit === "a") return n * 100;
  return n;
}

function sqmToInput(sqm) {
  if (!sqm) return { value: "", unit: "m2" };
  const v = Number(sqm);
  if (v >= 10000) return { value: (v / 10000).toString().replace(".", ","), unit: "ha" };
  if (v >= 100) return { value: (v / 100).toString().replace(".", ","), unit: "a" };
  return { value: Math.round(v).toString(), unit: "m2" };
}

const EMPTY_FORM = { name: "", location_name: "", latitude: "", longitude: "", area_value: "", area_unit: "m2", grape_variety: "", planted_year: "", phenology_phase: "" };

export default function Dashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [vineyards, setVineyards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/vineyards`, { withCredentials: true });
      setVineyards(r.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditingId(null); setForm(EMPTY_FORM); setShowForm(true); };

  const openEdit = (v, e) => {
    e?.stopPropagation();
    const ai = sqmToInput(v.area_sqm);
    setEditingId(v.id);
    setForm({
      name: v.name || "", location_name: v.location_name || "",
      latitude: v.latitude?.toString() || "", longitude: v.longitude?.toString() || "",
      area_value: ai.value, area_unit: ai.unit,
      grape_variety: v.grape_variety || "", planted_year: v.planted_year?.toString() || "",
      phenology_phase: v.phenology_phase || "",
    });
    setShowForm(true);
  };

  const onMapConfirm = ({ latitude, longitude, location_name }) => {
    setForm((f) => ({
      ...f,
      latitude: latitude.toFixed(6),
      longitude: longitude.toFixed(6),
      location_name: f.location_name || location_name,
    }));
    setShowMap(false);
    setShowForm(true);
    toast.success(t("dashboard.toasts.mapConfirmed"));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.latitude || !form.longitude) { toast.error(t("dashboard.toasts.missingRequired")); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        location_name: form.location_name?.trim() || "—",
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        area_sqm: toSqm(form.area_value, form.area_unit),
        grape_variety: form.grape_variety || null,
        planted_year: form.planted_year ? parseInt(form.planted_year) : null,
        phenology_phase: form.phenology_phase || null,
      };
      if (editingId) {
        await axios.patch(`${API}/vineyards/${editingId}`, payload, { withCredentials: true });
        toast.success(t("dashboard.toasts.updated"));
      } else {
        await axios.post(`${API}/vineyards`, payload, { withCredentials: true });
        toast.success(t("dashboard.toasts.added"));
      }
      setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); load();
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error(t("dashboard.toasts.sessionExpired"));
        setTimeout(() => { window.location.href = "/"; }, 1500);
      } else {
        const detail = err.response?.data?.detail;
        toast.error(detail ? t("dashboard.toasts.errorWithDetail", { detail }) : t("dashboard.toasts.saveError"));
        console.error("Save vineyard error:", err.response || err);
      }
    } finally { setSubmitting(false); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm(t("dashboard.confirmDelete"))) return;
    try {
      await axios.delete(`${API}/vineyards/${id}`, { withCredentials: true });
      toast.success(t("dashboard.toasts.deleted")); load();
    } catch { toast.error(t("dashboard.toasts.genericError")); }
  };

  return (
    <div className="min-h-screen pb-24" data-testid="dashboard-page">
      <Header title="Il Mio Vigneto" subtitle={t("dashboard.subtitle")} />
      <main className="max-w-2xl mx-auto px-4 py-5">
        <div className="vs-hero-photo mb-5 vs-fade-up" style={{ aspectRatio: "16 / 7" }}>
          <img src="https://images.pexels.com/photos/760281/pexels-photo-760281.jpeg?auto=compress&cs=tinysrgb&w=1200" alt={t("dashboard.heroImageAlt")} />
          <span className="badge">{t("common.heroTagline")}</span>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="heading text-2xl font-bold tracking-tight">{t("dashboard.title")}</h2>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              {t("dashboard.vineyardCount", { count: vineyards.length })}
            </p>
          </div>
          <button className="vs-btn-primary inline-flex items-center gap-2" onClick={openNew} data-testid="add-vineyard-btn">
            <Plus size={18} /> {t("dashboard.addButton")}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><div className="vs-spinner" /></div>
        ) : vineyards.length === 0 ? (
          <div className="vs-card p-8 text-center vs-grain" data-testid="empty-state">
            <Grape size={42} style={{ color: "var(--primary)" }} className="mx-auto mb-3" />
            <h3 className="heading text-xl font-bold mb-1">{t("dashboard.emptyState.title")}</h3>
            <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
              {t("dashboard.emptyState.description")}
            </p>
            <button className="vs-btn-primary" onClick={openNew} data-testid="empty-add-btn">
              <Plus size={18} className="inline mr-1" /> {t("dashboard.emptyState.addButton")}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {vineyards.map((v) => {
              const area = formatArea(v.area_sqm);
              return (
                <article key={v.id} className="vs-card p-4 vs-fade-up" style={{ borderColor: "var(--border)", position: "relative" }} data-testid={`vineyard-card-${v.id}`}>
                  <div className="absolute top-3 right-3 flex gap-2">
                    <button onClick={(e) => openEdit(v, e)} className="px-3 py-1.5 rounded-lg text-xs font-bold inline-flex items-center gap-1.5"
                      style={{ background: "var(--secondary)", border: "2px solid var(--border)", color: "var(--text-primary)" }}
                      aria-label={t("dashboard.card.editButton")} data-testid={`edit-vineyard-${v.id}`}>
                      <Pencil size={14} /> {t("dashboard.card.editButton")}
                    </button>
                    <button onClick={() => navigate(`/vigneto/${v.id}`)} className="px-3 py-1.5 rounded-lg text-xs font-bold inline-flex items-center gap-1.5"
                      style={{ background: "var(--gradient-vine)", color: "white", border: "2px solid var(--primary)" }}
                      aria-label={t("dashboard.card.openButton")} data-testid={`open-vineyard-${v.id}`}>
                      {t("dashboard.card.openButton")} <ArrowRight size={14} />
                    </button>
                  </div>

                  <div className="pr-2" style={{ paddingRight: 0 }}>
                    <div className="flex items-center gap-2 mb-1" style={{ paddingRight: "9rem" }}>
                      <Grape size={20} style={{ color: "var(--primary)" }} />
                      <h3 className="heading text-lg font-bold truncate" data-testid={`vineyard-name-${v.id}`}>{v.name}</h3>
                    </div>
                    <div className="flex items-center gap-1 text-sm mb-2" style={{ color: "var(--text-muted)" }}>
                      <MapPin size={14} /> {v.location_name}
                    </div>
                    {area && (
                      <div className="flex items-center gap-1.5 mb-2 flex-wrap" style={{ color: "var(--primary)" }} data-testid={`vineyard-area-${v.id}`}>
                        <Ruler size={14} />
                        <span className="text-sm font-extrabold">{area.primary}</span>
                        {area.secondary && (<span className="text-xs" style={{ color: "var(--text-muted)" }}>&nbsp;({area.secondary})</span>)}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {v.grape_variety && <span className="vs-pill">{v.grape_variety}</span>}
                      {v.phenology_phase && <span className="vs-pill" style={{ background: "var(--secondary)" }}>{getPhenologyLabel(v.phenology_phase, t)}</span>}
                      {v.planted_year && <span className="vs-pill">{t("dashboard.card.plantedYear", { year: v.planted_year })}</span>}
                    </div>
                  </div>

                  <button className="absolute bottom-3 right-3 p-2 rounded-lg" onClick={(e) => remove(v.id, e)}
                    style={{ background: "var(--surface)", border: "2px solid var(--border)" }}
                    aria-label={t("dashboard.card.deleteAriaLabel")} data-testid={`delete-vineyard-${v.id}`}>
                    <Trash2 size={16} style={{ color: "var(--accent)" }} />
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </main>

      {showForm && !showMap && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
          style={{ background: "rgba(17,20,17,0.5)" }}
          onClick={() => !submitting && setShowForm(false)}>
          <div className="w-full sm:max-w-lg vs-card p-5 sm:p-6 m-0 sm:m-4"
            style={{ borderRadius: "16px 16px 0 0", maxHeight: "90vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()} data-testid="vineyard-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="heading text-xl font-bold">{editingId ? t("dashboard.form.editTitle") : t("dashboard.form.newTitle")}</h3>
              <button onClick={() => setShowForm(false)} className="p-2"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="vs-label">{t("dashboard.form.nameLabel")}</label>
                <input className="vs-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("dashboard.form.namePlaceholder")} data-testid="form-name" required />
              </div>
              <div>
                <label className="vs-label">{t("dashboard.form.locationLabel")}</label>
                <input className="vs-input" value={form.location_name} onChange={(e) => setForm({ ...form, location_name: e.target.value })} placeholder={t("dashboard.form.locationPlaceholder")} data-testid="form-location" />
              </div>
              <div>
                <label className="vs-label">{t("dashboard.form.mapLabel")}</label>
                <button type="button" onClick={() => setShowMap(true)}
                  className="vs-btn-primary w-full inline-flex items-center justify-center gap-2"
                  style={{ background: form.latitude ? "var(--gradient-vine)" : "var(--surface)", color: form.latitude ? "white" : "var(--primary)", border: "2px solid var(--primary)" }}
                  data-testid="open-map-btn">
                  <MapIcon size={18} />
                  {form.latitude ? t("dashboard.form.mapPositionSet", { lat: parseFloat(form.latitude).toFixed(4), lng: parseFloat(form.longitude).toFixed(4) }) : t("dashboard.form.mapOpenButton")}
                </button>
                <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                  {t("dashboard.form.mapHelperText")}
                </p>
              </div>
              <div>
                <label className="vs-label">{t("dashboard.form.areaLabel")}</label>
                <div className="flex gap-2">
                  <input className="vs-input flex-1" type="text" inputMode="decimal" placeholder={t("dashboard.form.areaPlaceholder")}
                    value={form.area_value} onChange={(e) => setForm({ ...form, area_value: e.target.value })}
                    data-testid="form-area-value" />
                  <select className="vs-input" style={{ width: 110 }}
                    value={form.area_unit} onChange={(e) => setForm({ ...form, area_unit: e.target.value })}
                    data-testid="form-area-unit">
                    <option value="m2">{t("dashboard.form.unitSqm")}</option>
                    <option value="a">{t("dashboard.form.unitAre")}</option>
                    <option value="ha">{t("dashboard.form.unitHectare")}</option>
                  </select>
                </div>
                <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{t("dashboard.form.areaHelperText")}</p>
              </div>
              <div>
                <label className="vs-label">{t("dashboard.form.varietyLabel")}</label>
                <input className="vs-input" value={form.grape_variety} onChange={(e) => setForm({ ...form, grape_variety: e.target.value })} placeholder={t("dashboard.form.varietyPlaceholder")} data-testid="form-variety" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="vs-label">{t("dashboard.form.yearLabel")}</label>
                  <input className="vs-input" type="number" value={form.planted_year} onChange={(e) => setForm({ ...form, planted_year: e.target.value })} placeholder="2010" data-testid="form-year" />
                </div>
                <div>
                  <label className="vs-label">{t("dashboard.form.phenologyLabel")}</label>
                  <select className="vs-input" value={form.phenology_phase} onChange={(e) => setForm({ ...form, phenology_phase: e.target.value })} data-testid="form-phase">
                    <option value="">—</option>
                    {getPhenologyPhases(t).map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" className="vs-btn-secondary flex-1" onClick={() => setShowForm(false)} disabled={submitting}>{t("dashboard.form.cancelButton")}</button>
                <button type="submit" className="vs-btn-primary flex-1" disabled={submitting} data-testid="submit-vineyard">
                  {submitting ? t("dashboard.form.submitSaving") : (editingId ? t("dashboard.form.submitUpdate") : t("dashboard.form.submitCreate"))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <BottomNav />

      {showMap && (
        <MapPicker
          initialCenter={form.latitude ? [parseFloat(form.latitude), parseFloat(form.longitude)] : null}
          locationLabel={form.location_name}
          onCancel={() => setShowMap(false)}
          onConfirm={onMapConfirm}
        />
      )}
    </div>
  );
}
